import {
  CustomField,
  CustomFieldCheckboxFilter,
  CustomFieldDateFilter,
  CustomFieldDivider,
  CustomFieldDropdownFilter,
  BBTreeviewNode,
  NestedFilter
} from '../../types'
import { isCheckboxNode } from '../../utils/filters/filters.utils'
import get from 'lodash/get'

type Filterable = 'Dropdown' | 'Date' | 'Checkbox'

const isFilterableField = (field: CustomField) => ['Dropdown', 'Date', 'Checkbox'].includes(field.type)

type localStorageCFFilter = CustomFieldDateFilter | CustomFieldCheckboxFilter | CustomFieldDropdownFilter

function retrieveFilterFromLocalStorage<T extends localStorageCFFilter>(customField: CustomField) {
  let savedFilter = [] as T
  try {
    const filters = localStorage.getItem('filter-panel-filters')
    if (filters) {
      const cfFilters = JSON.parse(filters).custom_fields
      if (cfFilters) savedFilter = cfFilters.find((cfFilter: T) => cfFilter[0] === customField._id) || []
    }
  } catch (err) {
    console.warn('Error retrieving filter-panel-filters from local storage ', err)
  }
  return savedFilter
}

export function customFieldDividersToFiltersets(dividers: Array<CustomFieldDivider>): NestedFilter['panels'] {
  const a = dividers
    .map(divider => ({
      name: divider.name,
      heading: divider.name,
      id: divider.name,
      type: 'panelview',
      children: divider.custom_fields
        .filter(isFilterableField)
        .map(cf => mapFieldTypeToTreeNode(cf.type as Filterable, cf))
    }))
    .filter(divider => divider?.children?.length)
  return a as NestedFilter['panels']
}

function mapFieldTypeToTreeNode(fieldType: Filterable, customField: CustomField): BBTreeviewNode {
  switch (fieldType) {
    case 'Checkbox':
      return checkboxToTreeNodes(customField)
    case 'Dropdown':
      return dropdownToTreeNodes(customField)
    case 'Date':
      return dateToTreeNodes(customField)
    default:
      unreachable(fieldType)
  }
}

function checkboxToTreeNodes(customField: CustomField): BBTreeviewNode {
  const savedFilter = retrieveFilterFromLocalStorage<CustomFieldCheckboxFilter>(customField)
  const id = `customfield.${customField._id}`
  return {
    id,
    name: customField.name,
    selected: get(savedFilter, '[1].length') === 2,
    type: 'Checkbox',
    children: [
      {
        id: `${id}.isChecked`,
        groupingKey: customField._id,
        selected: get(savedFilter, '[1]')?.includes(true),
        type: 'Checkbox',
        value: true,
        name: 'Is Checked'
      },
      {
        id: `${id}.isNotChecked`,
        groupingKey: customField._id,
        selected: get(savedFilter, '[1]')?.includes(null),
        type: 'Checkbox',
        value: null,
        name: 'Is Not Checked'
      }
    ]
  }
}

function dropdownToTreeNodes(customField: CustomField): BBTreeviewNode {
  const savedFilter = retrieveFilterFromLocalStorage<CustomFieldDropdownFilter>(customField)

  const id = `customfield.${customField._id}`
  const children: Array<BBTreeviewNode> = [
    ...(customField.dropdown_options?.map(option => ({
      id: `${id}.option.${option.title}`,
      groupingKey: customField._id,
      selected: get(savedFilter, '[1]')?.includes(option.title),
      type: 'Checkbox' as const,
      value: option.title,
      name: option.title
    })) || []),
    {
      id: `${id}.option.not_set`,
      type: 'Checkbox',
      name: 'Not Set',
      selected: get(savedFilter, '[1]')?.includes(null),
      groupingKey: customField._id,
      value: null
    }
  ]
  return {
    id,
    name: customField.name,
    selected: children.every(child => (isCheckboxNode(child) ? child.selected : false)),
    type: 'Checkbox',
    children
  }
}

function dateToTreeNodes(customField: CustomField): BBTreeviewNode {
  const savedFilter = retrieveFilterFromLocalStorage<CustomFieldDateFilter>(customField)

  const id = `customfield.${customField._id}`
  return {
    id,
    name: customField.name,
    type: 'Date',
    children: [
      {
        id: `${id}.dates`,
        name: 'Select Dates',
        groupingKey: customField._id,
        value: {
          start: get(savedFilter, '[1].start')?.slice(0, 10) || '',
          end: get(savedFilter, '[1].end')?.slice(0, 10) || ''
        },
        type: 'Date'
      }
    ]
  }
}

/**
 * helper for an exhaustive switch statement
 * @param _
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function unreachable(_: never): never {
  throw new Error('type error. make sure you are handling all possible filter cases')
}
